import { apolloClient } from '@/vendor/apollo-client.ts'
import { BACKGROUND_OPERATION } from '@/utils/backgroundOperation'
import type { SyncableAction } from '@/stores/sync/createSyncEngine.ts'
import type { Language } from '@/stores/localization.ts'
import { UserLanguageType } from '../../../__generated__/graphql.ts'
import { GET_USER_LANGUAGE, UPDATE_USER_LANGUAGE } from '@/queries/userLanguage.ts'
import { UPDATE_USER_NOTIFICATION_SETTINGS } from '@/queries/notificationSettings.ts'
import { GET_USER_NOTIFICATION_TOKENS, UPDATE_USER_NOTIFICATION_TOKENS } from '@/queries'

export type NotificationSettings = {
  notificationNewReleasesFromPublishers: boolean
  notificationNewReleasesFromPublishersList: string
  notificationNewReleasesFromAuthors: boolean
  notificationNewReleasesFromAuthorsList: string
  notificationAddedReleaseDateToExistingReleaseCollectionItem: boolean
  notificationChangedReleaseDateOfExistingReleaseCollectionItem: boolean
  notificationReleaseCollectionItemAvailable: boolean
  notificationReleaseCollectionItemAlternativeVersionRelease: boolean
  notificationScannedItemAvailable: boolean
  notificationAtsumeRecommendations: boolean
}

// Read-only profile fields, cached in the same snapshot so Profile.vue renders offline
// from app-start data instead of its own network fetch. Not editable (no client
// mutation / no overlay action), so they simply pass through the overlay unchanged.
export type UserProfile = {
  name: string
  email: string
  createdAt: string | null
}

// Every server-backed user preference the client can write plus the read-only profile,
// held as one snapshot so an offline edit can be overlaid on it and drained when
// connectivity returns.
export type UserSettingsState = NotificationSettings &
  UserProfile & {
    language: Language
    notificationFcmTokens: string
  }

export type SettingsPatch = Partial<NotificationSettings & { language: Language }>

// A scalar edit (language / a notification field) — last-write-wins. FCM tokens are
// their own op because the field is a shared comma-joined list read-modified-written
// per device, so a blind patch would clobber other devices' tokens.
export type SettingsPatchAction = SyncableAction & { kind: 'patch'; patch: SettingsPatch }
export type FcmTokenAction = SyncableAction & {
  kind: 'fcm'
  op: 'add' | 'remove'
  token: string
  resultTokens?: string
}
export type UserSettingsAction = SettingsPatchAction | FcmTokenAction

export const DEFAULT_USER_SETTINGS: UserSettingsState = {
  language: 'de',
  notificationFcmTokens: '',
  name: '',
  email: '',
  createdAt: null,
  notificationNewReleasesFromPublishers: false,
  notificationNewReleasesFromPublishersList: '',
  notificationNewReleasesFromAuthors: false,
  notificationNewReleasesFromAuthorsList: '',
  notificationAddedReleaseDateToExistingReleaseCollectionItem: true,
  notificationChangedReleaseDateOfExistingReleaseCollectionItem: true,
  notificationReleaseCollectionItemAvailable: true,
  notificationReleaseCollectionItemAlternativeVersionRelease: true,
  notificationScannedItemAvailable: true,
  notificationAtsumeRecommendations: true,
}

function splitTokens(csv: string): string[] {
  return csv.split(',').filter((token) => token.length > 0)
}

function applyFcmOp(csv: string, op: 'add' | 'remove', token: string): string {
  const tokens = splitTokens(csv)
  if (op === 'add') {
    return tokens.includes(token) ? csv : [...tokens, token].join(',')
  }
  return tokens.filter((existing) => existing !== token).join(',')
}

// Lay the pending (unsynced) action overlay onto the base snapshot so the UI reflects
// offline edits immediately. Patches are last-write-wins; fcm ops fold into the CSV.
export function applyActionOverlay(
  base: UserSettingsState,
  actions: UserSettingsAction[],
): UserSettingsState {
  let result = { ...base }
  for (const action of actions) {
    if (action.synced) continue
    if (action.kind === 'patch') {
      result = { ...result, ...action.patch }
    } else {
      result = {
        ...result,
        notificationFcmTokens: applyFcmOp(result.notificationFcmTokens, action.op, action.token),
      }
    }
  }
  return result
}

// The self-scoped `users` query only ever returns the current user, so users[0].id is
// this user's Keystone id — resolved live at sync time (online only).
async function resolveUserId(): Promise<string | null> {
  const { data } = await apolloClient.query({
    query: GET_USER_LANGUAGE,
    fetchPolicy: 'network-only',
    context: BACKGROUND_OPERATION,
  })
  return data?.users?.[0]?.id ?? null
}

// Runs online only (gated by the engine's canSync). Scalar edits go straight through
// updateUser; an fcm op reads the current server token list first and merges, so a
// stale device write never drops another device's token.
export async function performSettingsSync(
  action: UserSettingsAction,
): Promise<Partial<UserSettingsAction> | void> {
  const userId = await resolveUserId()
  if (!userId) throw new Error('user settings sync: no user id')

  if (action.kind === 'patch') {
    const { language, ...notification } = action.patch
    if (language !== undefined) {
      await apolloClient.mutate({
        mutation: UPDATE_USER_LANGUAGE,
        variables: {
          where: { id: userId },
          data: { language: language === 'en' ? UserLanguageType.En : UserLanguageType.De },
        },
        context: BACKGROUND_OPERATION,
      })
    }
    if (Object.keys(notification).length > 0) {
      await apolloClient.mutate({
        mutation: UPDATE_USER_NOTIFICATION_SETTINGS,
        variables: { where: { id: userId }, data: notification },
        context: BACKGROUND_OPERATION,
      })
    }
    return
  }

  const { data } = await apolloClient.query({
    query: GET_USER_NOTIFICATION_TOKENS,
    fetchPolicy: 'network-only',
    context: BACKGROUND_OPERATION,
  })
  const currentTokens = data?.users?.[0]?.notificationFcmTokens ?? ''
  const nextTokens = applyFcmOp(currentTokens, action.op, action.token)
  const result = await apolloClient.mutate({
    mutation: UPDATE_USER_NOTIFICATION_TOKENS,
    variables: { where: { id: userId }, data: { notificationFcmTokens: nextTokens } },
    context: BACKGROUND_OPERATION,
  })
  return { resultTokens: result.data?.updateUser?.notificationFcmTokens ?? nextTokens }
}
